import { useEffect, useState } from "react";
import { hitApi, copyData, commonToast } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

export default function Links({ pageTitle }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copiedIdx, setCopiedIdx] = useState(-1);

    useEffect(() => {
        hitApi({ url: "affiliate/links", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setData(resp.data || null);
        });
    }, []);

    const copy = (url, idx) => {
        copyData(url);
        commonToast("success", "Link copied to clipboard.");
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(-1), 1500);
    };

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Affiliate Links">
            {loading ? (
                <LinksSkeleton />
            ) : !data?.affiliate ? (
                <div className="pxl-status-banner pxl-status-banner--warning">No affiliate profile found.</div>
            ) : data.affiliate.status !== "approved" ? (
                <div className="pxl-aff-pending">
                    <div className="pxl-aff-pending-icon"><IconClock /></div>
                    <div>
                        <h3>Your account is <span className="pxl-aff-status-badge">{data.affiliate.status}</span></h3>
                        <p>Your referral links activate as soon as an admin approves your application. We'll email you the moment that happens.</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Referral code highlight card */}
                    <div className="pxl-aff-code-card">
                        <div className="pxl-aff-code-card-orb" />
                        <div className="pxl-aff-code-card-body">
                            <div className="pxl-aff-code-label">Your referral code</div>
                            <div className="pxl-aff-code-value">{data.affiliate.referralCode}</div>
                            <div className="pxl-aff-code-hint">
                                Add <code>?ref={data.affiliate.referralCode}</code> to any PixaLab URL to attribute the visit to you.
                            </div>
                        </div>
                        <button className="pxl-aff-code-copy" onClick={() => copy(data.affiliate.referralCode, -2)}>
                            <IconCopy /> {copiedIdx === -2 ? "Copied!" : "Copy code"}
                        </button>
                    </div>

                    {/* Links list */}
                    <div className="pxl-aff-panel">
                        <div className="pxl-aff-panel-header">
                            <div>
                                <h3 className="pxl-aff-panel-title">Your referral links</h3>
                                <div className="pxl-aff-panel-sub">
                                    Click <strong>Copy</strong> to grab the link, or <strong>Open</strong> to preview where your visitors will land.
                                </div>
                            </div>
                            <span className="pxl-aff-panel-count">{(data.links || []).length} links</span>
                        </div>
                        <ul className="pxl-aff-link-list">
                            {(data.links || []).map((l, i) => (
                                <li className="pxl-aff-link-row" key={i}>
                                    <div className="pxl-aff-link-icon">{iconForLabel(l.label)}</div>
                                    <div className="pxl-aff-link-meta">
                                        <div className="pxl-aff-link-label">{l.label}</div>
                                        <div className="pxl-aff-link-url" title={l.url}>{l.url}</div>
                                    </div>
                                    <div className="pxl-aff-link-actions">
                                        <a href={l.url} target="_blank" rel="noopener noreferrer" className="pxl-aff-link-btn-secondary" title="Open in a new tab">
                                            <IconExternal /> Open
                                        </a>
                                        <button
                                            className={`pxlCommon-btn pxl-aff-link-btn-primary${copiedIdx === i ? " is-copied" : ""}`}
                                            onClick={() => copy(l.url, i)}
                                        >
                                            {copiedIdx === i ? <><IconCheck /> Copied</> : <><IconCopy /> Copy</>}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Sharing tips */}
                    <div className="pxl-aff-tip">
                        <div className="pxl-aff-tip-icon"><IconBulb /></div>
                        <div>
                            <strong>Pro tip:</strong> Share the deep checkout links (Basic / Pro / Business) when you're recommending a specific plan —
                            they convert better than the generic pricing page because the visitor lands one click away from purchase.
                        </div>
                    </div>
                </>
            )}
        </AffiliatePageLayout>
    );
}

function LinksSkeleton() {
    return (
        <>
            {/* Code card skeleton */}
            <div className="pxl-sk-panel" style={{ marginBottom: 20 }}>
                <div className="pxl-sk-panel-body">
                    <span className="pxl-sk" style={{ width: 100, height: 10, display: 'block' }} />
                    <span className="pxl-sk" style={{ width: 160, height: 22, display: 'block', marginTop: 8 }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <span className="pxl-sk" style={{ width: 110, height: 36, borderRadius: 8, display: 'inline-block' }} />
                    </div>
                </div>
            </div>

            {/* Links panel skeleton */}
            <div className="pxl-sk-panel">
                <div className="pxl-sk-panel-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="pxl-sk" style={{ width: 140, height: 13, display: 'inline-block' }} />
                    <span className="pxl-sk" style={{ width: 28, height: 20, borderRadius: 10, display: 'inline-block' }} />
                </div>
                <div className="pxl-sk-panel-body">
                    {[0, 1, 2].map(i => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 16, marginBottom: 16, borderBottom: i < 2 ? '1px solid #f0f2f7' : 'none' }}>
                            {/* Icon circle */}
                            <span className="pxl-sk" style={{ width: 32, height: 32, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />
                            {/* Label + url */}
                            <div style={{ flex: 1 }}>
                                <span className="pxl-sk" style={{ width: '45%', height: 13, display: 'block' }} />
                                <span className="pxl-sk" style={{ width: '60%', height: 10, display: 'block', marginTop: 6 }} />
                            </div>
                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                <span className="pxl-sk" style={{ width: 64, height: 32, borderRadius: 6, display: 'inline-block' }} />
                                <span className="pxl-sk" style={{ width: 64, height: 32, borderRadius: 6, display: 'inline-block' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

/* Pick an icon based on the link label so each row reads at a glance. */
function iconForLabel(label) {
    const l = (label || "").toLowerCase();
    if (l.includes("home")) return <IconHome />;
    if (l.includes("pricing")) return <IconTag />;
    if (l.includes("checkout")) return <IconCart />;
    return <IconLink />;
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconHome()    { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function IconTag()     { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function IconCart()    { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>; }
function IconLink()    { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>; }
function IconCopy()    { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>; }
function IconCheck()   { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function IconExternal(){ return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>; }
function IconClock()   { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function IconBulb()    { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/></svg>; }
